import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ImageList,
  ImageListItem,
  Stack,
  Typography,
} from '@mui/material'
import { apiClient } from '../services/api'
import { apiError } from './errors'
import { useToast } from './useToast'
import AssetUploadButton from './AssetUploadButton'

interface AssetMeta {
  id: string
  url: string
  filename: string
  contentType: string
  byteSize: number
}

/**
 * Browse + reuse previously uploaded assets for an event, or upload a new one.
 * Resolves the chosen asset URL via onPick.
 */
export default function AssetPicker({
  eventId,
  onClose,
  onPick,
}: {
  eventId: string
  onClose: () => void
  onPick: (url: string) => void
}) {
  const { notify } = useToast()
  const [assets, setAssets] = useState<AssetMeta[]>([])
  const [loading, setLoading] = useState(true)

  const load = () =>
    apiClient
      .get<AssetMeta[]>(`/admin/events/${eventId}/assets`)
      .then((r) => setAssets(r.data.filter((a) => a.contentType.startsWith('image/'))))
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Choose an image</DialogTitle>
      <DialogContent>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <AssetUploadButton
            eventId={eventId}
            onUploaded={(url) => {
              onPick(url)
              onClose()
            }}
          />
        </Stack>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        ) : assets.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No images yet — upload one above.
          </Typography>
        ) : (
          <ImageList cols={4} gap={8} sx={{ m: 0 }}>
            {assets.map((a) => (
              <ImageListItem
                key={a.id}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'primary.main' },
                }}
                onClick={() => {
                  onPick(a.url)
                  onClose()
                }}
              >
                <Box
                  component="img"
                  src={a.url}
                  alt={a.filename}
                  loading="lazy"
                  sx={{ width: '100%', height: 110, objectFit: 'cover' }}
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}
